import { useEffect, useMemo, useState } from 'react';
import { onOrdersForDaySnapshot } from '@paulownia/shared/services/orders';
import { getCachedProducts } from '@paulownia/shared/services/products-cache';
import { getTenantById } from '@paulownia/shared/services/tenant';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import { type Order } from '@paulownia/shared/types';
import { type Product, calculateOrderTotal } from '@paulownia/shared/types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '@paulownia/shared/components/ui/button';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Input } from '@paulownia/shared/components/ui/input';
import { Link } from 'react-router-dom';
import { Clock, Search, X, StickyNote, AlertTriangle, Repeat } from 'lucide-react';
import { SwipeableOrderCard } from './SwipeableOrderCard';
import { StatusCard } from './StatusCard';
import { OrderDetailModal } from './OrderDetailModal';
import { OrderEditDialog } from './OrderEditDialog';
import { ValidationQueueDialog } from './ValidationQueueDialog';
import { useOrderManager } from '@paulownia/shared/hooks/useOrderManager';
import { getTodayKey, getPreviousDay, getNextDay } from '@paulownia/shared/lib/date-utils';
import { useTenant } from '@paulownia/shared/lib/tenant-context';

type Filter = 'all' | 'awaiting_validation' | 'pending' | 'ready' | 'done';



export function OrdersBoard() {
  const { currentTenantId } = useTenant();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedDate, setSelectedDate] = useState(getTodayKey());
  const [tenantSettings, setTenantSettings] = useState<{
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  } | null>(null);

  // Filtre de recherche par nom
  const [searchName, setSearchName] = useState('');

  // Modal pour voir les détails d'une commande
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);

  // Derived state for the modal
  const viewingOrder = useMemo(() => {
    if (!viewingOrderId) return null;
    return orders.find(o => o.id === viewingOrderId) || null;
  }, [orders, viewingOrderId]);

  // Modal pour éditer une commande
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  // Modal de validation en file
  const [isValidationQueueOpen, setIsValidationQueueOpen] = useState(false);

  const {
    handleStatusChange,
    handleTogglePaid,
    handleDeleteOrder,
    handleMarkAllPreparedAndReady,
    toggleLinePrepared
  } = useOrderManager();

  useEffect(() => {
    if (!currentTenantId) {
      setOrders([]);
      return;
    }
    const unsub = onOrdersForDaySnapshot(selectedDate, (o) => {
      setOrders(o); // Inclure toutes les commandes y compris les récupérées
    }, currentTenantId);
    return () => unsub();
  }, [selectedDate, currentTenantId]);

  useEffect(() => {
    if (!currentTenantId) {
      setProducts([]);
      return;
    }

    // Initial load with cache
    getCachedProducts(currentTenantId).then(setProducts).catch((error) => {
      reportError(error, { context: 'OrdersBoard.loadProducts', tenantId: currentTenantId });
    });

    // Refresh every 5 minutes
    const interval = setInterval(() => {
      getCachedProducts(currentTenantId).then(setProducts).catch((error) => {
        reportError(error, { context: 'OrdersBoard.refreshProducts', tenantId: currentTenantId });
      });
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentTenantId]);

  // Load tenant settings for section/subSection ordering
  useEffect(() => {
    if (!currentTenantId) {
      setTenantSettings(null);
      return;
    }
    getTenantById(currentTenantId)
      .then((tenant) => {
        if (tenant) {
          setTenantSettings(tenant.settings || null);
        }
      })
      .catch((error) => {
        reportError(error, { context: 'OrdersBoard.loadTenantSettings', tenantId: currentTenantId });
      });
  }, [currentTenantId]);

  const filtered = useMemo(() => {
    let result = orders;

    // Filtre par statut
    if (filter === 'all') result = result.filter((o) => o.status !== 'done');
    else if (filter === 'awaiting_validation') result = result.filter((o) => o.status === 'awaiting_validation');
    else if (filter === 'pending') result = result.filter((o) => o.status === 'pending');
    else if (filter === 'ready') result = result.filter((o) => o.status === 'ready');
    else if (filter === 'done') result = result.filter((o) => o.status === 'done');

    // Filtre par nom
    if (searchName.trim()) {
      const search = searchName.toLowerCase().trim();
      result = result.filter((o) => o.customerName.toLowerCase().includes(search));
    }

    return result;
  }, [orders, filter, searchName]);

  const awaitingValidationCount = useMemo(() => orders.filter((o) => o.status === 'awaiting_validation').length, [orders]);
  const pendingCount = useMemo(() => orders.filter((o) => o.status === 'pending').length, [orders]);
  const readyCount = useMemo(() => orders.filter((o) => o.status === 'ready').length, [orders]);
  const doneCount = useMemo(() => orders.filter((o) => o.status === 'done').length, [orders]);
  const activeCount = useMemo(() => orders.filter((o) => o.status !== 'done').length, [orders]);



  async function onStatusChangeWrapper(orderId: string, newStatus: 'ready' | 'done') {
    await handleStatusChange(orderId, newStatus);
    if (newStatus === 'done') {
      setViewingOrderId(null);
    }
  }

  async function onTogglePaidWrapper(orderId: string, currentPaidStatus: boolean) {
    await handleTogglePaid(orderId, currentPaidStatus);
  }

  async function onTogglePreparedWrapper(order: Order, lineIndex: number) {
    // toggleLinePrepared gère la logique de revert pending
    await toggleLinePrepared(order, lineIndex);
  }

  async function onDeleteWrapper(orderId: string) {
    await handleDeleteOrder(orderId);
    setViewingOrderId(null);
  }

  function openEditModal(order: Order) {
    setViewingOrderId(null);
    setEditingOrder(order);
  }



  const selectedDateObj = new Date(selectedDate + 'T12:00:00');
  const isToday = selectedDate === getTodayKey();

  return (
    <div className="pb-16">
      <div className="px-4 py-4 border-b bg-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-semibold">Commandes</h1>
            <p className="text-sm text-gray-500">
              {format(new Date(), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}
            </p>
          </div>
          <Link to={`/dashboard/new?date=${selectedDate}`}>
            <Button>+ Ajouter</Button>
          </Link>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(getPreviousDay(selectedDate))}>
            ←
          </Button>
          <div className="flex-1 flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-9 w-full text-base"
              style={{ fontSize: '16px' }}
            />
            {!isToday && (
              <Button variant="default" size="sm" onClick={() => setSelectedDate(getTodayKey())}>
                Aujourd'hui
              </Button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(getNextDay(selectedDate))}>
            →
          </Button>
        </div>

        <div className="text-sm font-medium text-gray-700 mb-3">
          {format(selectedDateObj, "EEEE d MMMM yyyy", { locale: fr })}
        </div>

        {/* Validation alert banner */}
        {awaitingValidationCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-semibold">
                {awaitingValidationCount} nouvelle{awaitingValidationCount > 1 ? 's' : ''} commande{awaitingValidationCount > 1 ? 's' : ''} à valider
              </span>
            </div>
            <p className="text-sm text-amber-700 mt-1">
              Ces commandes attendent votre confirmation avant d'être préparées.
            </p>
            <Button
              size="sm"
              className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => setIsValidationQueueOpen(true)}
            >
              Valider les commandes
            </Button>
          </div>
        )}

        <div className="grid grid-cols-5 gap-2 mb-3">
          <StatusCard
            count={activeCount}
            label="En cours"
            color="blue"
            isActive={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          <StatusCard
            count={awaitingValidationCount}
            label="À valider"
            color="amber"
            isActive={filter === 'awaiting_validation'}
            onClick={() => setFilter('awaiting_validation')}
          />
          <StatusCard
            count={pendingCount}
            label="À préparer"
            color="orange"
            isActive={filter === 'pending'}
            onClick={() => setFilter('pending')}
          />
          <StatusCard
            count={readyCount}
            label="Prêtes"
            color="green"
            isActive={filter === 'ready'}
            onClick={() => setFilter('ready')}
          />
          <StatusCard
            count={doneCount}
            label="Terminées"
            color="gray"
            isActive={filter === 'done'}
            onClick={() => setFilter('done')}
          />
        </div>

        {/* Recherche par nom */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Rechercher par nom..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="pl-9 h-9"
          />
          {searchName && (
            <button
              onClick={() => setSearchName('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Liste des commandes - une ligne par commande */}
      <div className="p-4">
        {filtered.length === 0 && (
          <div className="text-center text-sm text-gray-500 py-8">Aucune commande</div>
        )}

        <div className="space-y-2">
          {filtered.map((order) => {
            const total = calculateOrderTotal(order.lines, products);
            const pickupDate = new Date(order.pickupAtISO);
            const pickupTime = pickupDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

            return (
              <SwipeableOrderCard
                key={order.id}
                onSwipeComplete={() => handleStatusChange(order.id, 'done')}
                enabled={order.status !== 'done' && order.status !== 'awaiting_validation'}
                confirmMessage={order.status !== 'ready' ? "Cette commande n'est pas marquée comme prête. Confirmer qu'elle est récupérée ?" : undefined}
              >
                <button
                  onClick={() => setViewingOrderId(order.id)}
                  className={`w-full p-3 rounded-lg border-2 bg-white text-left transition-all hover:shadow-md ${order.status === 'done'
                    ? 'border-gray-300 bg-gray-50 opacity-70'
                    : order.status === 'ready'
                      ? 'border-green-300 bg-green-50/50'
                      : order.status === 'awaiting_validation'
                        ? 'border-amber-300 bg-amber-50/50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Heure */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="font-semibold text-gray-700">{pickupTime}</span>
                    </div>

                    {/* Nom client */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium break-words">
                        {order.customerName}
                      </div>
                    </div>

                    {/* Prix */}
                    <div className="shrink-0">
                      {total !== null ? (
                        <span className="font-bold text-green-700">{total.toFixed(2)}€</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </div>

                  {/* Badges - seconde ligne */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {order.note && (
                      <span title="Note">
                        <StickyNote className="h-4 w-4 text-amber-500" />
                      </span>
                    )}
                    {order.recurringOrderId && (
                      <span title="Commande récurrente" className="flex items-center gap-1 text-orange-600">
                        <Repeat className="h-3.5 w-3.5" />
                      </span>
                    )}
                    {order.paid && (
                      <Badge className="bg-green-600 text-xs">Payé</Badge>
                    )}
                    {order.status === 'awaiting_validation' && <Badge className="bg-amber-500 text-xs">Nouvelle</Badge>}
                    {order.status === 'pending' && <Badge variant="secondary" className="text-xs">À préparer</Badge>}
                    {order.status === 'ready' && <Badge variant="green" className="text-xs">Prête</Badge>}
                    {order.status === 'done' && <Badge variant="outline" className="text-xs text-gray-500">Récupérée</Badge>}
                  </div>
                </button>
              </SwipeableOrderCard>
            );
          })}
        </div>
      </div>

      {/* Modal de détails de la commande */}
      <OrderDetailModal
        order={viewingOrder}
        isOpen={!!viewingOrder}
        onClose={() => setViewingOrderId(null)}
        products={products}
        onStatusChange={onStatusChangeWrapper}
        onMarkAllPreparedAndReady={handleMarkAllPreparedAndReady}
        onTogglePaid={onTogglePaidWrapper}
        onTogglePrepared={onTogglePreparedWrapper}
        onEdit={openEditModal}
        onDelete={onDeleteWrapper}
      />

      <OrderEditDialog
        order={editingOrder}
        isOpen={!!editingOrder}
        onClose={() => setEditingOrder(null)}
        products={products}
        tenantSettings={tenantSettings}
      />

      <ValidationQueueDialog
        isOpen={isValidationQueueOpen}
        onClose={() => setIsValidationQueueOpen(false)}
        orders={orders}
        products={products}
      />
    </div>
  );
}
